import React from 'react'
import {
  extractClassIdFromSearch,
  extractSiteIdFromSearch,
  getCurrentSearchString,
} from 'utils/routing/urlHelpers'
import { findUserContextSiteById } from 'utils/lms/userContextHelpers'

/**
 * Determines if the MarkComplete component should be hidden based on missing programTrack
 * @param {Object} userContextManager - The raw user context manager state
 * @param {boolean} isK5Url - Whether the current URL is for K5/elementary
 * @param {boolean} isMiddleSchoolUrl - Whether the current URL is for middle school
 * @returns {boolean} True if the component should be hidden, false otherwise
 */
export const shouldHideMarkCompleteForMissingProgramTrack = (
  userContextManager,
  isK5Url,
  isMiddleSchoolUrl,
  locationSearch = getCurrentSearchString(),
) => {
  // Only apply logic for K5 or Middle School URLs
  if (!isK5Url && !isMiddleSchoolUrl) {
    return false
  }

  // Class context means training is accessed from a class, not as a catalog preview
  if (extractClassIdFromSearch(locationSearch)) {
    return false
  }

  // Prefer explicit site from URL over persisted active site
  const activeSiteIdFromContext = isK5Url
    ? userContextManager?.elementaryActiveSiteId
    : userContextManager?.middleSchoolActiveSiteId
  const siteIdFromUrl = extractSiteIdFromSearch(locationSearch)
  const activeSiteId = siteIdFromUrl ?? activeSiteIdFromContext

  // Return false (don't hide) if no active site ID or no sites data
  if (!activeSiteId || !userContextManager?.sites) {
    return false
  }

  const activeSite = findUserContextSiteById(
    userContextManager.sites,
    activeSiteId,
  )

  if (!activeSite) {
    return false
  }

  // Determine the program key based on URL type
  const programKey = isK5Url ? 'k5SelDigital' : 'mspDigital'
  const program = activeSite.programs?.[programKey]

  // Hide component if programTrack is null, undefined, or missing
  return !program?.preferences?.programTrack
}

/**
 * Checks if the current pathname matches the training path pattern
 * @param {string} pathname - The pathname to check (defaults to window.location.pathname)
 * @returns {boolean} True if pathname matches product/<program>/trainings/<training> pattern
 */
export const matchesTrainingPathPattern = (
  pathname = window.location.pathname,
) => {
  const pattern = /^\/product\/[^/]+\/trainings\/[^/]+/
  return pattern.test(pathname)
}

export const manageContent = (
  content,
  ContentComponents,
  getContentComponent,
  callback,
) => {
  if (!content || content.length <= 0) {
    return null
  }

  const contentComponents = []
  let inAccordion = false
  let accordionContent = []
  let accordionData = {}
  for (const child of content) {
    const { name, data } = child
    const ContentComponent = ContentComponents[name]

    if (name === 'accordion' && !inAccordion) {
      inAccordion = true
      accordionData = data
      accordionContent = []
      continue
    }
    if (name === 'accordion' && inAccordion) {
      inAccordion = false
      contentComponents.push(
        <ContentComponent
          key={`content-${contentComponents.length}-${name}`}
          {...accordionData}
        >
          {accordionContent}
        </ContentComponent>,
      )
      continue
    }
    if (inAccordion) {
      accordionContent.push(getContentComponent(child))
      continue
    }
    contentComponents.push(getContentComponent(child, contentComponents.length))
  }
  return callback(contentComponents)
}
